import React, { useEffect, useState, useMemo, useCallback } from 'react';
import api from '../utils/axios';
import { useNavigate } from 'react-router-dom';
import { Eye, Download, Trash2, FileText, Search, Filter, X } from 'lucide-react';
import { monthNames } from '../constants';
import Dropdown from '../components/Dropdown';
import CTable from '../components/shared/c-table';
import CModal from '../components/shared/c-modal';
import usePersistedState from '../hooks/usePersistedState';
import { useAuth } from '../contexts/AuthContext';
import { toastErrorOnce } from '../utils/permissionToast';
import { canExportMeezan } from '../utils/reportPermissions';
import { responseErrorMessage } from '../utils/responseError';

const MEEZAN = 'meezan_bank_transfer_sheet';
const MEEZAN_LABEL = 'Meezan Bank Transfer Sheet';
// A virtual Meezan row stands in for the transfer sheet of the salary sheet it came from.
const rowType = (r) => r.virtualType || r.type;

const Reports = () => {
  const [reportTypes, setReportTypes] = useState([]);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const { hasPermission } = useAuth();
  const canMeezan = canExportMeezan(hasPermission);
  const canDelete = hasPermission('reports', 'delete');
  const [searchTerm, setSearchTerm] = usePersistedState('reports.search', '');
  const [filters, setFilters] = usePersistedState('reports.filters', {
    type: '',
    month: '',
    year: ''
  });
  const navigate = useNavigate();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteReportId, setDeleteReportId] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [showExportError, setShowExportError] = useState(false);
  const [exportErrorMsg, setExportErrorMsg] = useState('');

  // GET /api/reports/types sends { id, name } per type.
  const reportTypeLabels = useMemo(() => Object.fromEntries(reportTypes.map(rt => [rt.id, rt.name])), [reportTypes]);
  const typeLabel = useCallback(
    (r) => (rowType(r) === MEEZAN ? MEEZAN_LABEL : reportTypeLabels[r.type] || r.type),
    [reportTypeLabels]
  );

  // Every salary sheet can be exported in Meezan's transfer format, which is its own row.
  // The export records nothing in the audit log, so there is no "was exported" to wait for.
  const allRows = useMemo(() => reports.flatMap((r) => (
    r.type === 'salary_sheet' && canMeezan
      ? [r, { ...r, id: `${r.id}-meezan`, sourceId: r.id, virtualType: MEEZAN, isVirtual: true }]
      : [r]
  )), [reports, canMeezan]);

  const filteredReports = useMemo(() => {
    const searchLower = searchTerm.toLowerCase();
    return allRows.filter(report => {
      const matchesSearch = searchTerm === '' ||
        typeLabel(report).toLowerCase().includes(searchLower) ||
        monthNames[report.month - 1]?.toLowerCase().includes(searchLower) ||
        report.year?.toString().includes(searchLower) ||
        new Date(report.created_at).toLocaleDateString().includes(searchLower);
      const matchesType = filters.type === '' || rowType(report) === filters.type;
      const matchesMonth = filters.month === '' || report.month === Number.parseInt(filters.month, 10);
      const matchesYear = filters.year === '' || report.year === Number.parseInt(filters.year, 10);
      return matchesSearch && matchesType && matchesMonth && matchesYear;
    });
  }, [allRows, searchTerm, filters, typeLabel]);
  
  const availableYears = useMemo(() => {
    const years = [...new Set(reports.map(r => r.year))].sort((a, b) => b - a);
    return years;
  }, [reports]);
  
  const clearFilters = () => {
    setSearchTerm('');
    setFilters({ type: '', month: '', year: '' });
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [typesRes, reportsRes] = await Promise.all([
          api.get('/api/reports/types'),
          api.get('/api/reports'),
        ]);
        setReportTypes(typesRes.data.data || []);
        setReports(reportsRes.data.data || []);
      } catch (err) {
        toastErrorOnce('Failed to load reports');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleExport = async (reportId, month, year) => {
    try {
      // Use the backend domain for API calls in production
      const baseUrl = process.env.NODE_ENV === 'production' 
        ? 'https://backend-hrms.bytecorp.io' 
        : '';
      const url = `${baseUrl}/api/reports/${reportId}/export?format=csv`;
      
      const res = await fetch(url, {
        credentials: 'include'
      });
      if (!res.ok) {
        setExportErrorMsg(await responseErrorMessage(res, 'Export failed'));
        setShowExportError(true);
        return;
      }
      const blob = await res.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = `salary-sheet-${year}-${String(month).padStart(2, '0')}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(downloadUrl);
    } catch (err) {
      setExportErrorMsg('Export failed');
      setShowExportError(true);
    }
  };

  const handleExportMeezan = async (reportId, month, year) => {
    try {
      // Use the backend domain for API calls in production
      const baseUrl = process.env.NODE_ENV === 'production' 
        ? 'https://backend-hrms.bytecorp.io' 
        : '';
      const url = `${baseUrl}/api/reports/${reportId}/export-meezan-bank-transfer`;
      
      const res = await fetch(url, {
        credentials: 'include'
      });
      if (!res.ok) {
        setExportErrorMsg(await responseErrorMessage(res, 'Export failed'));
        setShowExportError(true);
        return;
      }
      const blob = await res.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = `meezan-bank-transfer-${year}-${String(month).padStart(2, '0')}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(downloadUrl);
    } catch (err) {
      setExportErrorMsg('Export failed');
      setShowExportError(true);
    }
  };

  const handleDelete = (reportId) => {
    setDeleteReportId(reportId);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    setDeleting(true);
    try {
      await api.delete(`/api/reports/${deleteReportId}`);
      setReports(reports => reports.filter(r => r.id !== deleteReportId));
      setShowDeleteModal(false);
      setDeleteReportId(null);
    } catch (err) {
      setExportErrorMsg('Failed to delete report');
      setShowExportError(true);
    } finally {
      setDeleting(false);
    }
  };

  // A missing timestamp would otherwise render as the Unix epoch.
  const fmt = (d) => (d ? new Date(d).toLocaleString() : '-');
  const isMeezan = (r) => r.type === MEEZAN;

  // A virtual Meezan row is projected from a salary sheet: it can be exported
  // but has nothing stored to view or delete, so those two actions are inert.
  const columns = [
    {
      key: 'type',
      header: 'Type',
      render: typeLabel,
    },
    { key: 'month', header: 'Month', render: (r) => monthNames[r.month - 1] },
    { key: 'year', header: 'Year' },
    { key: 'created_at', header: 'Created At', render: (r) => fmt(r.created_at) },
    {
      key: 'updated_at',
      header: 'Updated At',
      render: (r) => fmt(r.updated_at || r.created_at),
    },
    {
      key: 'actions',
      header: 'Actions',
      align: 'right',
      render: (r) => {
        const viewDisabled = r.isVirtual || isMeezan(r);
        // An old stored Meezan-type row (the API no longer accepts that type) keeps its
        // export disabled, as it always was.
        const exportDisabled = !r.isVirtual && isMeezan(r);
        const deleteDisabled = r.isVirtual || isMeezan(r);
        // The transfer sheet is account numbers and salaries: salaries:read_all only.
        const showExport = !(r.isVirtual || isMeezan(r)) || canMeezan;
        return (
          <div className="flex items-center justify-end space-x-2">
            <button
              type="button"
              className={`text-primary-600 hover:text-primary-900 p-1 rounded transition ring-0 focus:ring-2 focus:ring-primary-300 hover:bg-primary-50${viewDisabled ? ' opacity-50 cursor-not-allowed' : ''}`}
              title="View Details"
              onClick={() => !viewDisabled && navigate(`/reports/${r.id}`)}
              disabled={viewDisabled}
            >
              <Eye className="h-4 w-4" />
            </button>
            {showExport && (
              <button
                type="button"
                className={`text-green-600 hover:text-green-900 p-1 rounded transition ring-0 focus:ring-2 focus:ring-green-300 hover:bg-green-50${exportDisabled ? ' opacity-50 cursor-not-allowed' : ''}`}
                title={r.isVirtual ? 'Export Meezan Bank Sheet' : (r.type === 'salary_sheet' ? 'Export Salary Sheet' : 'Export')}
                data-testid="reports-action-export"
                onClick={() => {
                  if (exportDisabled) return;
                  return r.isVirtual
                    ? handleExportMeezan(r.sourceId, r.month, r.year)
                    : handleExport(r.id, r.month, r.year);
                }}
                disabled={exportDisabled}
              >
                <Download className="h-4 w-4" />
              </button>
            )}
            {canDelete && (
              <button
                type="button"
                className={`text-danger-600 hover:text-danger-900 p-1 rounded transition ring-0 focus:ring-2 focus:ring-danger-300 hover:bg-danger-50${deleteDisabled ? ' opacity-50 cursor-not-allowed' : ''}`}
                title="Delete"
                data-testid="reports-action-delete"
                onClick={() => !deleteDisabled && handleDelete(r.id)}
                disabled={deleteDisabled}
              >
                <Trash2 className="h-4 w-4" />
              </button>
            )}
          </div>
        );
      },
    },
  ];

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 mb-1">Generated Reports</h2>
          <p className="text-gray-600">View, export, and manage your generated reports</p>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-soft p-6">
        <div className="flex flex-col lg:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <input
                type="text"
                placeholder="Search reports..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
          </div>
          
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative">
              <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4 pointer-events-none z-10" />
              {/* !pl-10: Dropdown's own pl-3 won the cascade, so the funnel sat on "All". */}
              <Dropdown
                value={filters.type}
                onChange={(e) => setFilters(prev => ({ ...prev, type: e.target.value }))}
                className="!pl-10"
                data-testid="reports-type-filter"
              >
                <option value="">All Types</option>
                {reportTypes.map(type => (
                  <option key={type.id} value={type.id}>{type.name}</option>
                ))}
                {canMeezan && <option value={MEEZAN}>{MEEZAN_LABEL}</option>}
              </Dropdown>
            </div>
            
            <Dropdown
              value={filters.month}
              onChange={(e) => setFilters(prev => ({ ...prev, month: e.target.value }))}
            >
              <option value="">All Months</option>
              {monthNames.map((name, index) => (
                <option key={index + 1} value={index + 1}>{name}</option>
              ))}
            </Dropdown>
            
            <Dropdown
              value={filters.year}
              onChange={(e) => setFilters(prev => ({ ...prev, year: e.target.value }))}
            >
              <option value="">All Years</option>
              {availableYears.map(year => (
                <option key={year} value={year}>{year}</option>
              ))}
            </Dropdown>
            
            {(searchTerm || filters.type || filters.month || filters.year) && (
              <button
                onClick={clearFilters}
                className="px-3 py-2 text-gray-600 hover:text-gray-800 flex items-center gap-1 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                <X className="h-4 w-4" />
                Clear
              </button>
            )}
          </div>
        </div>
        
        <div className="mt-4 text-sm text-gray-600">
          Showing {filteredReports.length} of {allRows.length} reports
        </div>
      </div>
      
      <CTable
        columns={columns}
        data={filteredReports}
        loading={loading}
        rowKey="id"
        getRowProps={(r) => ({
          'data-testid': `reports-row-${r.id}`,
          // Virtual Meezan rows are derived from a salary sheet, not stored
          // reports, and stay visually distinct from the real rows.
          className: r.isVirtual ? 'bg-gray-50 hover:bg-gray-100' : '',
        })}
        emptyMessage={
          <>
            <FileText className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">
              {reports.length === 0 ? 'No reports found' : 'No reports match your filters'}
            </h3>
            <p className="mt-1 text-sm text-gray-500">
              {reports.length === 0
                ? 'Reports you generate will appear here.'
                : 'Try adjusting your search or filters.'}
            </p>
          </>
        }
      />

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <CModal
          onClose={() => setShowDeleteModal(false)}
          labelledBy="delete-report-title"
          dismissible={!deleting}
          className="bg-white rounded-lg shadow-lg w-full max-w-md p-6 relative"
        >
          <button
            aria-label="Close"
            className="absolute top-3 right-3 text-gray-400 hover:text-gray-700 text-2xl font-bold"
            onClick={() => setShowDeleteModal(false)}
            disabled={deleting}
          >
            &times;
          </button>
          <h2 id="delete-report-title" className="text-xl font-bold mb-4 text-danger-700">Delete Report</h2>
          <p className="mb-4 text-gray-700">
            Are you sure you want to delete this report? <b>This action cannot be undone.</b>
          </p>
          <div className="flex justify-end gap-2">
            <button
              className="px-4 py-2 rounded bg-gray-100 text-gray-700 hover:bg-gray-200"
              onClick={() => setShowDeleteModal(false)}
              disabled={deleting}
            >
              Cancel
            </button>
            <button
              className="px-4 py-2 rounded bg-danger-600 text-white hover:bg-danger-700 font-semibold"
              onClick={confirmDelete}
              disabled={deleting}
            >
              {deleting ? 'Deleting...' : 'Delete'}
            </button>
          </div>
        </CModal>
      )}
      {/* Export Error Modal */}
      {showExportError && (
        <CModal
          onClose={() => setShowExportError(false)}
          labelledBy="export-error-title"
          className="bg-white rounded-lg shadow-lg w-full max-w-md p-6 relative"
        >
          <button
            aria-label="Close"
            className="absolute top-3 right-3 text-gray-400 hover:text-gray-700 text-2xl font-bold"
            onClick={() => setShowExportError(false)}
          >
            &times;
          </button>
          <h2 id="export-error-title" className="text-xl font-bold mb-4 text-danger-700">Error</h2>
          <p className="mb-4 text-gray-700">{exportErrorMsg}</p>
          <div className="flex justify-end gap-2">
            <button
              className="px-4 py-2 rounded bg-primary-600 text-white hover:bg-primary-700 font-semibold"
              onClick={() => setShowExportError(false)}
            >
              Close
            </button>
          </div>
        </CModal>
      )}
    </div>
  );
};

export default Reports; 